import { index, modelOptions, mongoose, post, prop, PropType, Ref } from "@typegoose/typegoose";
import { z } from "zod";
import type { Organization } from "../organization/organization.js";
import { E164PhoneNumber, getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { ScdDocument } from "../scdDocument/scdDocument.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    NotificationAddressFieldsWithDefaults,
    NotificationAddressSchemaTypes,
} from "../types/notificationAddress.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";
import { QuietHoursWindow } from "./subSchemas/quietHoursWindow.js";

export { QuietHoursWindow } from "./subSchemas/quietHoursWindow.js";

const SCHEMA_KEY = "notificationAddresses" as const;

/**
 * A phone number and its delivery policy. Global and deduplicated by `address` — a number can belong
 * to many organizations, so consent and preferences live on the number, not on any subscription.
 * SCD Type-2: consent and preference changes keep a versioned history; the stable identity across
 * versions is `recordId`, so other collections (memberships) reference the number by recordId.
 */
@index({ address: 1 }, { unique: true, partialFilterExpression: { versionIsCurrent: true, deleted: false } })
@index({ memberOrganizations: 1 }, { partialFilterExpression: { versionIsCurrent: true, deleted: false } })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "notificationAddresses" as const satisfies typeof SCHEMA_KEY,
        _id: true,
    },
})
@post<SchemaBase<NotificationAddress>>(...addSchemaKeyHook(SCHEMA_KEY))
export class NotificationAddress extends ScdDocument<NotificationAddress, NotificationAddressFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /**
     * E.164 phone number; unique among current, non-deleted versions (see the partial index above).
     * Response-only: the number is set at enrollment (via the phoneNumber body), never edited in place.
     */
    @validate({ zodValidator: E164PhoneNumber, responseOnly: true })
    @prop({ type: () => String, required: true })
    public address!: string;

    /** Optional friendly name for the number, e.g. "John Smith". Global to the number. */
    @validate({ zodValidator: z.string().min(1).optional() })
    @prop({ type: () => String, required: false })
    public name?: string;

    /** Whether the number has been verified. Response-only: set by the verification flow, not clients. */
    @validate({ zodValidator: z.boolean(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public verified: boolean = false;

    /** Preferred locale for rendering messages, e.g. "en-US". */
    @validate({ zodValidator: z.string().min(1), hasDefault: true })
    @prop({ type: () => String, required: true, default: "en-US" })
    public locale: string = "en-US";

    /** When set (via a STOP reply), all sends to this number are suppressed. Response-only: set by the STOP flow. */
    @validate({ zodValidator: z.boolean(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public optedOut: boolean = false;

    /** Optional quiet-hours window; while open, standard alerts defer until it ends. Nullable so an update can clear it. */
    @validate({ childClass: QuietHoursWindow, childClassTransform: (object) => object.nullable().optional() })
    @prop({ type: () => QuietHoursWindow, required: false, _id: false })
    public quietHours?: QuietHoursWindow;

    /** Whether critical alerts may pierce quiet hours for this number. */
    @validate({ zodValidator: z.boolean(), hasDefault: true })
    @prop({ type: () => Boolean, required: true, default: true })
    public allowCriticalWarningsToInterruptQuietHours: boolean = true;

    // Internal org↔address association, never part of any API contract (a globally deduplicated number
    // must not disclose the other orgs tied to it). Intentionally NOT @validate-decorated so it stays
    // out of every generated DTO variant; it is read/written directly on the model by the enroll flows.
    /** Organizations this number is enrolled in; the direct org↔address association. */
    @prop({ type: () => [mongoose.Types.ObjectId], required: true, default: [], ref: "Organization" }, PropType.ARRAY)
    public memberOrganizations: Ref<Organization>[] = [];

    /** Organizations this number has opted out of; enrollment into these is refused. */
    @prop({ type: () => [mongoose.Types.ObjectId], required: true, default: [], ref: "Organization" }, PropType.ARRAY)
    public optedOutOrganizations: Ref<Organization>[] = [];

    static getters = {} as const satisfies Record<string, (self: NotificationAddress) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<NotificationAddressSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<NotificationAddress, NotificationAddressFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

NotificationAddress.dtoValidators = getZodSchemaFromClass(
    NotificationAddress,
) as unknown as DtoValidatorTypes<NotificationAddressSchemaTypes>;
